/**
 * Client-side GLB processing using @gltf-transform.
 *
 * Runs in the browser after upload to:
 * 1. Analyze triangle count + texture sizes
 * 2. Warn if over device budget
 * 3. Optionally simplify geometry + resize textures
 * 4. Re-upload the optimized version
 *
 * This is a fallback for when the server-side optimizer worker isn't deployed.
 */

import { Document, WebIO } from '@gltf-transform/core';
import { dedup, prune, quantize } from '@gltf-transform/functions';
import { ALL_EXTENSIONS } from '@gltf-transform/extensions';

// Device budget thresholds
export const DEVICE_BUDGETS = {
  quest: { maxTriangles: 500_000, maxTextureSize: 1024, maxFileSizeMB: 50 },
  desktop: { maxTriangles: 5_000_000, maxTextureSize: 4096, maxFileSizeMB: 500 },
};

const IS_QUEST = typeof navigator !== 'undefined' && /Quest|OculusBrowser|GathrXR/i.test(navigator.userAgent);

export interface AssetAnalysis {
  triangles: number;
  vertices: number;
  meshCount: number;
  maxTextureWidth: number;
  maxTextureHeight: number;
  textureCount: number;
  fileSizeMB: number;
  overBudget: boolean;
  warnings: string[];
}

/**
 * Analyze a GLB file and return stats + budget warnings.
 */
export async function analyzeGLB(glbArrayBuffer: ArrayBuffer): Promise<AssetAnalysis> {
  const io = new WebIO().registerExtensions(ALL_EXTENSIONS);
  const doc = await io.readBinary(new Uint8Array(glbArrayBuffer));

  let triangles = 0;
  let vertices = 0;
  let meshCount = 0;
  let maxTextureWidth = 0;
  let maxTextureHeight = 0;

  for (const mesh of doc.getRoot().listMeshes()) {
    meshCount++;
    for (const prim of mesh.listPrimitives()) {
      const posAccessor = prim.getAttribute('POSITION');
      if (posAccessor) vertices += posAccessor.getCount();
      const indices = prim.getIndices();
      if (indices) {
        triangles += indices.getCount() / 3;
      } else if (posAccessor) {
        triangles += posAccessor.getCount() / 3;
      }
    }
  }

  const textures = doc.getRoot().listTextures();
  for (const tex of textures) {
    const size = tex.getSize();
    if (size) {
      maxTextureWidth = Math.max(maxTextureWidth, size[0]);
      maxTextureHeight = Math.max(maxTextureHeight, size[1]);
    }
  }

  const fileSizeMB = glbArrayBuffer.byteLength / (1024 * 1024);
  const budget = IS_QUEST ? DEVICE_BUDGETS.quest : DEVICE_BUDGETS.desktop;
  const warnings: string[] = [];

  if (triangles > budget.maxTriangles) {
    warnings.push(`${Math.round(triangles / 1000)}K triangles exceeds ${Math.round(budget.maxTriangles / 1000)}K budget`);
  }
  if (maxTextureWidth > budget.maxTextureSize || maxTextureHeight > budget.maxTextureSize) {
    warnings.push(`Textures ${maxTextureWidth}x${maxTextureHeight} exceed ${budget.maxTextureSize}px limit`);
  }
  if (fileSizeMB > budget.maxFileSizeMB) {
    warnings.push(`File ${fileSizeMB.toFixed(1)}MB exceeds ${budget.maxFileSizeMB}MB limit`);
  }

  return {
    triangles: Math.round(triangles),
    vertices,
    meshCount,
    maxTextureWidth,
    maxTextureHeight,
    textureCount: textures.length,
    fileSizeMB: Math.round(fileSizeMB * 10) / 10,
    overBudget: warnings.length > 0,
    warnings,
  };
}

/**
 * Optimize a GLB file client-side.
 * Returns the optimized GLB as an ArrayBuffer.
 */
export async function optimizeGLB(
  glbArrayBuffer: ArrayBuffer,
  options?: { maxTextureSize?: number }
): Promise<{ buffer: ArrayBuffer; stats: { beforeMB: number; afterMB: number; reductionPct: number } }> {
  const io = new WebIO().registerExtensions(ALL_EXTENSIONS);
  const doc = await io.readBinary(new Uint8Array(glbArrayBuffer));

  await doc.transform(
    dedup(),
    prune(),
    quantize(),
  );

  const optimized = await io.writeBinary(doc);
  const beforeMB = glbArrayBuffer.byteLength / (1024 * 1024);
  const afterMB = optimized.byteLength / (1024 * 1024);

  return {
    buffer: optimized.buffer as ArrayBuffer,
    stats: {
      beforeMB: Math.round(beforeMB * 10) / 10,
      afterMB: Math.round(afterMB * 10) / 10,
      reductionPct: Math.round((1 - afterMB / beforeMB) * 100),
    },
  };
}
